import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { firstValueFrom } from 'rxjs';
import { HELP_API_URL } from '../help-api.token';

/**
 * Low-level client for the help HTTP API: attaches the Cognito ID token (the API's JWT authorizer
 * checks `aud` against the client id, which only the ID token carries) and centralises error
 * logging so the feature services stay declarative.
 */
@Injectable({
  providedIn: 'root',
})
export class HelpApiClient {
  private readonly httpClient = inject(HttpClient);
  private readonly helpApiUrl = inject(HELP_API_URL);

  public async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    return this.request('reading from the help API', (headers) =>
      firstValueFrom(this.httpClient.get<T>(`${this.helpApiUrl}${path}`, { headers, params }))
    );
  }

  public async post<T>(path: string, body: unknown): Promise<T> {
    return this.request('posting to the help API', (headers) =>
      firstValueFrom(this.httpClient.post<T>(`${this.helpApiUrl}${path}`, body, { headers }))
    );
  }

  private async request<T>(action: string, run: (headers: HttpHeaders) => Promise<T>): Promise<T> {
    try {
      const headers = await this.authHeaders();
      return await run(headers);
    } catch (error) {
      console.error(`Error ${action}:`, error);
      return Promise.reject(error);
    }
  }

  private async authHeaders(): Promise<HttpHeaders> {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      throw new Error('Not authenticated');
    }
    return new HttpHeaders({ Authorization: `Bearer ${idToken}` });
  }
}
